import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "react-toastify";
import {
  API_ops_assistant_shortcuts,
  API_create_ops_assistant_shortcut,
  API_update_ops_assistant_shortcut,
  API_delete_ops_assistant_shortcut,
  API_reorder_ops_assistant_shortcuts,
} from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";

const MAX_SHORTCUTS = 10;
const LABEL_MAX = 60;
const PROMPT_MAX = 500;

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 640,
  maxHeight: "90vh",
  overflow: "hidden",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

export default function ManageShortcutsModal({
  open,
  onClose,
  shortcuts,
  setShortcuts,
  templates,
}) {
  const [activeTab, setActiveTab] = useState("yours");
  const [editingId, setEditingId] = useState(null); // id, or "new", or null
  const [formLabel, setFormLabel] = useState("");
  const [formPrompt, setFormPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  // Switch back to "yours" each time the modal opens
  useEffect(() => {
    if (open) {
      setActiveTab(shortcuts.length === 0 ? "templates" : "yours");
      setEditingId(null);
      setFormLabel("");
      setFormPrompt("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const atCap = shortcuts.length >= MAX_SHORTCUTS;

  const sortedShortcuts = useMemo(() => {
    return [...(shortcuts || [])].sort((a, b) => {
      if (a.display_order !== b.display_order) return a.display_order - b.display_order;
      return a.id - b.id;
    });
  }, [shortcuts]);

  // Keys of templates the user has already added (by label) so we can
  // disable the Add button for duplicates.
  const ownedTemplateKeys = useMemo(() => {
    const set = new Set();
    const existingLabels = new Set((shortcuts || []).map((s) => s.label));
    for (const t of templates || []) {
      if (existingLabels.has(t.label)) set.add(t.key);
    }
    return set;
  }, [shortcuts, templates]);

  const openNewForm = () => {
    if (atCap) {
      toast.error(`You can have at most ${MAX_SHORTCUTS} shortcuts.`);
      return;
    }
    setEditingId("new");
    setFormLabel("");
    setFormPrompt("");
  };

  const openEditForm = (shortcut) => {
    setEditingId(shortcut.id);
    setFormLabel(shortcut.label);
    setFormPrompt(shortcut.prompt);
  };

  const closeForm = () => {
    setEditingId(null);
    setFormLabel("");
    setFormPrompt("");
  };

  const handleSaveForm = async () => {
    const label = formLabel.trim();
    const prompt = formPrompt.trim();
    if (!label || !prompt) {
      toast.error("Label and prompt are both required.");
      return;
    }
    setBusy(true);
    try {
      if (editingId === "new") {
        const res = await axios.post(
          API_create_ops_assistant_shortcut,
          { label, prompt },
          getDefaultAxiosConfig()
        );
        setShortcuts((prev) => [...prev, res.data]);
        toast.success("Shortcut added.");
      } else {
        const res = await axios.patch(
          API_update_ops_assistant_shortcut(editingId),
          { label, prompt },
          getDefaultAxiosConfig()
        );
        setShortcuts((prev) => prev.map((s) => (s.id === editingId ? res.data : s)));
        toast.success("Shortcut updated.");
      }
      closeForm();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        Object.values(err?.response?.data || {})[0]?.[0] ||
        "Failed to save shortcut.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (shortcut) => {
    if (!window.confirm(`Delete "${shortcut.label}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(
        API_delete_ops_assistant_shortcut(shortcut.id),
        getDefaultAxiosConfig()
      );
      setShortcuts((prev) => prev.filter((s) => s.id !== shortcut.id));
      toast.success("Shortcut deleted.");
    } catch (err) {
      toast.error("Failed to delete shortcut.");
    } finally {
      setBusy(false);
    }
  };

  // Serialize reorder POSTs and coalesce rapid clicks. Rapid up/down
  // presses would otherwise fire concurrent POSTs whose responses could
  // return out of order, letting a stale order overwrite the final one
  // server-side. Pattern: keep the latest desired order in a ref; a single
  // worker drains it one POST at a time, always using the most recent
  // snapshot. The UI stays responsive because the optimistic update runs
  // immediately on every click.
  const pendingOrderRef = useRef(null);
  const reorderWorkerRunningRef = useRef(false);

  const persistOrder = async (reordered) => {
    // Optimistic UI update
    setShortcuts(reordered);
    pendingOrderRef.current = reordered;

    if (reorderWorkerRunningRef.current) return;
    reorderWorkerRunningRef.current = true;
    try {
      while (pendingOrderRef.current) {
        const snapshot = pendingOrderRef.current;
        // Clear before awaiting so later clicks can set a newer snapshot
        // that this worker will pick up on the next loop iteration.
        pendingOrderRef.current = null;
        try {
          await axios.post(
            API_reorder_ops_assistant_shortcuts,
            {
              shortcut_orders: snapshot.map((s, idx) => ({
                id: s.id,
                display_order: idx,
              })),
            },
            getDefaultAxiosConfig()
          );
        } catch (err) {
          toast.error("Failed to save order. Refreshing.");
          try {
            const res = await axios.get(
              API_ops_assistant_shortcuts,
              getDefaultAxiosConfig()
            );
            // Only apply server truth if the user hasn't queued a newer
            // reorder during the refresh window. Otherwise we'd clobber
            // their latest optimistic state, and the loop would still
            // post their intent on the next iteration — leaving UI and
            // server briefly diverged.
            if (!pendingOrderRef.current) {
              setShortcuts(res.data?.shortcuts || []);
            }
          } catch {
            // keep optimistic state; user will see stale order until next mount
          }
          // Don't break — if the user queued a newer intent during the
          // failure/refresh window, the while-loop will pick it up and
          // post it. If nothing was queued, pendingOrderRef is null and
          // the loop exits naturally.
        }
      }
    } finally {
      reorderWorkerRunningRef.current = false;
    }
  };

  const moveShortcut = (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= sortedShortcuts.length) return;
    const reordered = [...sortedShortcuts];
    [reordered[index], reordered[newIdx]] = [reordered[newIdx], reordered[index]];
    const withOrder = reordered.map((s, idx) => ({ ...s, display_order: idx }));
    persistOrder(withOrder);
  };

  const addFromTemplate = async (template) => {
    if (atCap) {
      toast.error(`You can have at most ${MAX_SHORTCUTS} shortcuts.`);
      return;
    }
    if (ownedTemplateKeys.has(template.key)) return;
    setBusy(true);
    try {
      const res = await axios.post(
        API_create_ops_assistant_shortcut,
        { label: template.label, prompt: template.prompt },
        getDefaultAxiosConfig()
      );
      setShortcuts((prev) => [...prev, res.data]);
      toast.success(`Added "${template.label}".`);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to add shortcut.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1500 }}>
      <Box sx={modalStyle}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Manage Shortcuts</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Shortcuts show as chips in Rovis. {shortcuts.length} / {MAX_SHORTCUTS} used.
            </p>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-5">
          <TabButton
            active={activeTab === "yours"}
            onClick={() => setActiveTab("yours")}
            label={`Your Shortcuts (${shortcuts.length}/${MAX_SHORTCUTS})`}
          />
          <TabButton
            active={activeTab === "templates"}
            onClick={() => setActiveTab("templates")}
            label="Templates"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-gray-50">
          {activeTab === "yours" && (
            <div>
              {editingId !== null && (
                <ShortcutForm
                  label={formLabel}
                  prompt={formPrompt}
                  onLabelChange={setFormLabel}
                  onPromptChange={setFormPrompt}
                  onSave={handleSaveForm}
                  onCancel={closeForm}
                  busy={busy}
                  isNew={editingId === "new"}
                />
              )}

              {editingId === null && (
                <button
                  onClick={openNewForm}
                  disabled={atCap}
                  className={`w-full mb-3 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-dashed text-sm font-medium ${
                    atCap
                      ? "border-gray-300 text-gray-400 cursor-not-allowed"
                      : "border-violet-400 text-violet-700 hover:bg-violet-50"
                  }`}
                >
                  <AddIcon fontSize="small" />
                  {atCap ? `At max (${MAX_SHORTCUTS})` : "New shortcut"}
                </button>
              )}

              {sortedShortcuts.length === 0 && editingId === null && (
                <div className="text-center text-sm text-gray-500 py-6">
                  No shortcuts yet. Create a custom one above, or browse the{" "}
                  <button
                    className="text-violet-700 underline"
                    onClick={() => setActiveTab("templates")}
                  >
                    templates
                  </button>
                  .
                </div>
              )}

              {sortedShortcuts.map((s, idx) => (
                <ShortcutRow
                  key={s.id}
                  shortcut={s}
                  onEdit={() => openEditForm(s)}
                  onDelete={() => handleDelete(s)}
                  onMoveUp={() => moveShortcut(idx, -1)}
                  onMoveDown={() => moveShortcut(idx, +1)}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < sortedShortcuts.length - 1}
                  disabled={busy || editingId !== null}
                />
              ))}
            </div>
          )}

          {activeTab === "templates" && (
            <div>
              <p className="text-xs text-gray-500 mb-3">
                Pick from Rightwork's curated prompts. Added shortcuts are fully editable.
              </p>
              {(templates || []).map((t) => {
                const owned = ownedTemplateKeys.has(t.key);
                return (
                  <div
                    key={t.key}
                    className="bg-white border border-gray-200 rounded-lg p-3 mb-2 flex items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800">{t.label}</div>
                      <div className="text-xs text-gray-500 mt-1 line-clamp-2">{t.prompt}</div>
                    </div>
                    <button
                      onClick={() => addFromTemplate(t)}
                      disabled={owned || atCap || busy}
                      className={`shrink-0 text-xs px-3 py-1.5 rounded-md flex items-center gap-1 ${
                        owned
                          ? "bg-green-50 text-green-700 border border-green-200 cursor-default"
                          : atCap
                          ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                          : "bg-violet-700 text-white hover:bg-violet-800"
                      }`}
                    >
                      {owned ? (
                        <>
                          <CheckIcon style={{ fontSize: 14 }} />
                          Added
                        </>
                      ) : (
                        <>
                          <AddIcon style={{ fontSize: 14 }} />
                          Add
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Box>
    </Modal>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[2px] ${
        active
          ? "border-violet-700 text-violet-700"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label}
    </button>
  );
}

function ShortcutRow({
  shortcut,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  disabled,
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 mb-2 flex items-start gap-2">
      <div className="flex flex-col">
        <IconButton
          size="small"
          onClick={onMoveUp}
          disabled={!canMoveUp || disabled}
          sx={{ padding: "2px" }}
        >
          <ArrowUpwardIcon style={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={onMoveDown}
          disabled={!canMoveDown || disabled}
          sx={{ padding: "2px" }}
        >
          <ArrowDownwardIcon style={{ fontSize: 14 }} />
        </IconButton>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-800 truncate">{shortcut.label}</div>
        <div className="text-xs text-gray-500 mt-1 line-clamp-2">{shortcut.prompt}</div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <IconButton size="small" onClick={onEdit} disabled={disabled} sx={{ padding: "4px" }}>
          <EditIcon style={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" onClick={onDelete} disabled={disabled} sx={{ padding: "4px" }}>
          <DeleteIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>
    </div>
  );
}

function ShortcutForm({
  label,
  prompt,
  onLabelChange,
  onPromptChange,
  onSave,
  onCancel,
  busy,
  isNew,
}) {
  return (
    <div className="bg-white border border-violet-300 rounded-lg p-4 mb-3">
      <div className="text-sm font-semibold text-gray-800 mb-3">
        {isNew ? "New shortcut" : "Edit shortcut"}
      </div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Label
        <span className="text-gray-400 font-normal ml-2">
          {label.length} / {LABEL_MAX}
        </span>
      </label>
      <input
        type="text"
        value={label}
        onChange={(e) => onLabelChange(e.target.value.slice(0, LABEL_MAX))}
        placeholder="e.g. Check overtime risk"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-violet-500"
      />
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Prompt sent to Rovis
        <span className="text-gray-400 font-normal ml-2">
          {prompt.length} / {PROMPT_MAX}
        </span>
      </label>
      <textarea
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value.slice(0, PROMPT_MAX))}
        placeholder="e.g. Look at this week's draft schedule and flag any employees approaching the overtime threshold…"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-violet-500"
        rows={4}
      />
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={busy || !label.trim() || !prompt.trim()}
          className="px-4 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:bg-gray-300"
        >
          {isNew ? "Create" : "Save"}
        </button>
      </div>
    </div>
  );
}
